// src/components/PerformanceSection.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ConstantsContext } from "../index";
import CoinLogo from "./CoinLogo";
import { numberToAmericanFormat } from "./functions";

export default function PerformanceSection({ userDetails, _apiConnect }) {
  const [staticText] = useContext(ConstantsContext);
  const [pnlList, setPnlList] = useState([]);
  const [filterMonthsArr, setFilterMonthsArr] = useState([]);
  const [filterMonth, setFilterMonth] = useState("");

  function formatDate(inputDate) {
    if (!inputDate) return "";
    const [year, month] = inputDate.split("-");
    const date = new Date(year, month - 1);
    const monthName = date.toLocaleString("default", { month: "short" });
    return `${monthName} ${year}`.toUpperCase();
  }
  function _api_get_pnl_public_callback(_data) {
    if (_data && _data.status_code === 2) {
      setFilterMonthsArr(_data.monthsArray || []);
      if (!filterMonth && _data.monthsArray?.length) {
        setFilterMonth(_data.monthsArray[0]);
      }
      setPnlList(_data.data || []);
    }
  }
  const _api_get_pnl_public = (_month) => {
    const data = [
      { key: "loggined_token", value: userDetails?.loggined_token },
      { key: "action", value: "get_pnl_public" },
      { key: "filterByMonthValue", value: _month },
    ];
    _apiConnect(staticText.path_darts, data, _api_get_pnl_public_callback);
  };

  useEffect(() => {
    _api_get_pnl_public("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => pnlList || [], [pnlList]);

  return (
    <section id="performance" className="relative">
      <div className="rounded-2xl border border-white/10 bg-white/5">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4">
          <h3 className="text-sm font-semibold tracking-tight">
            {filterMonth ? `${formatDate(filterMonth)} Performance Snapshot` : "Performance Snapshot"}
          </h3>
          <PeriodDropdown
            months={filterMonthsArr}
            value={filterMonth}
            onChange={(m) => {
              setFilterMonth(m);
              _api_get_pnl_public(m);
            }}
          />
        </div>
        <PerformanceTable rows={rows} monthLabel={formatDate(filterMonth)} />
      </div>
    </section>
  );
}

function PeriodDropdown({ months = [], value, onChange }) {
  return (
    <div className="relative">
      <select
        className="appearance-none rounded-xl border border-white/10 px-3 py-2 pr-8 text-xs text-slate-100"
        style={{ backgroundColor: "#0B3A53" }}
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
      >
        {months.map((m, idx) => (
          <option key={m} value={m}>
            {idx === 0 ? "Current Month" : idx === 1 ? "Last Month" : m}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">▾</span>
    </div>
  );
}

function PerformanceTable({ rows = [], monthLabel }) {
  const [expanded, setExpanded] = useState(false);
  const coins = useMemo(() => (rows || []).filter((r) => r.type === "coin"), [rows]);
  const visible = useMemo(() => (expanded ? coins : coins.slice(0, 2)), [coins, expanded]);

  const addDayToMonthYear = (str) => str.replace(" ", " 1, ");

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="sticky top-0 z-10 bg-slate-900/60 backdrop-blur">
          <tr className="text-left text-slate-300">
            <th className="px-4 py-3 font-medium">Asset</th>
            <th className="px-4 py-3 font-medium text-right">Month</th>
            <th className="px-4 py-3 font-medium text-right">MTD P&amp;L %</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r, idx) => {
            const pnlVal = r.burned === 1 ? -99.9999 : Number(r.pnl || 0);
            const positive = pnlVal >= 0;
            const openFmt = numberToAmericanFormat(r.open_usd, 5);
            const closeFmt = numberToAmericanFormat(r.close_usd, 5);

            return (
              <tr key={idx} className="border-t border-white/5">
                <td className="px-4 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <CoinLogo coinPair={`${r.name}-USD`} />
                    </div>
                    <div>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[11px] text-slate-400">
                        Price as of {monthLabel ? addDayToMonthYear(monthLabel) : ""}&nbsp;${openFmt}
                      </div>
                      <div className="text-[11px] text-slate-400">Price Now&nbsp;${closeFmt}</div>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4 text-right text-slate-300">{r.month}</td>

                <td className="px-4 py-4">
                  <div className="text-right font-semibold">
                    <span className={positive ? "text-emerald-400" : "text-rose-400"}>
                      {pnlVal.toFixed(2)}%
                    </span>
                  </div>
                  <div
                    style={{
                      marginTop: 8,
                      height: 6,
                      width: "100%",
                      borderRadius: 9999,
                      background: "rgba(255,255,255,0.10)",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        borderRadius: 9999,
                        width: `${Math.min(Math.abs(pnlVal), 100)}%`,
                        backgroundColor: positive ? "#10b981" : "#ef4444",
                        transition: "width .2s ease",
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {coins.length > 2 && (
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-emerald-400 hover:text-emerald-300"
          >
            {expanded ? "Show less" : `Show ${coins.length - 2} more`}
          </button>
        </div>
      )}
    </div>
  );
}
